import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { convertHexToRGBA, truncate } from '@utils/usefulFuncs';
import { extractDocTypes, getDocTypeDisplayValue } from '@utils/doc';
import { SelectableDocumentsList } from '@containers/documents/SelectableDocumentsList';
import { useDocsCache } from '@hooks/features/useDocsCache';
import { Loading } from '@ui/Loading';
import {
  assembleReportingPeriodTypes,
  REPORTING_PERIOD_AVAILABLE_TYPES,
  ReportingPeriodFilter,
} from './components/ReportingPeriodFilter';

const Wrapper = styled.div``;
const TickerWrapper = styled.div`
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.text};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.14s;
  min-width: 66px;
  text-align: center;

  &:hover {
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.1)};
    border: 1px solid ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 1)};

    * {
      color: ${({ theme }) => theme.themeColors.primaryCtaButton};
    }
  }

  &.selected {
    border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};

    * {
      color: ${({ theme }) => theme.themeColors.buttonText};
    }
  }
`;
const TickersButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
`;

function isCompanyActive(company, activeCompany) {
  if (activeCompany?.doc_tag_id) {
    return company.doc_tag_id === activeCompany.doc_tag_id;
  }
  return company.symbol === activeCompany.symbol;
}

const CompanyButton = ({ company, activeCompany, onClick }) => {
  return (
    <TickerWrapper
      onClick={() => onClick(company)}
      className={isCompanyActive(company, activeCompany) ? 'selected' : ''}
    >
      <Body5 bold>{company.symbol || truncate(company.name, 16)}</Body5>
    </TickerWrapper>
  );
};

function isDocumentTypeActive(documentType, activeDocumentType) {
  return documentType === activeDocumentType;
}

const DocumentTypeButton = ({ value, activeDocumentType, onClick }) => {
  return (
    <TickerWrapper
      onClick={() => onClick(value)}
      className={isDocumentTypeActive(value, activeDocumentType) ? 'selected' : ''}
    >
      <Body5 bold>{getDocTypeDisplayValue(value)}</Body5>
    </TickerWrapper>
  );
};

function sortByDate(docs) {
  return docs.sort((a, b) => new Date(b.report_date || b.published_at) - new Date(a.report_date || a.published_at));
}

const noneType = 'None';

export const StandaloneDocumentSelection = ({ selectedDocs, onSelect, onRemoveSelect, companies, singleDocMode }) => {
  const prevCompanies = React.useRef(companies);

  const [activeCompany, setActiveCompany] = React.useState(companies[0] || null);

  const [activeDocumentType, setActiveDocumentType] = React.useState(null);
  const [documentTypes, setDocumentTypes] = React.useState([]);

  const [filters] = React.useState({});
  const { docsData, loadingCompanies } = useDocsCache(companies, activeCompany, filters);

  const [filteredAndSortedDocs, setFilteredAndSortedDocs] = React.useState(null);

  const [reportingPeriodFilterValues, setReportingPeriodFilterValues] = React.useState({});
  const [reportingPeriodTypes, setReportingPeriodTypes] = React.useState([]);

  React.useEffect(() => {
    if (companies?.length > 0) {
      const newCompanies = companies.filter(
        (c) =>
          !prevCompanies.current.some((pc) =>
            pc.company_id ? pc.company_id === c.company_id : pc.doc_tag_id === c.doc_tag_id
          )
      );
      if (newCompanies?.length === 1) {
        setActiveCompany(newCompanies[0]);
      }
    }
    prevCompanies.current = companies;
  }, [companies]);

  React.useEffect(() => {
    if (docsData?.docs) {
      setDocumentTypes(extractDocTypes(docsData.docs));
      const nextReportingPeriodTypes = assembleReportingPeriodTypes(docsData.docs);
      setReportingPeriodTypes(nextReportingPeriodTypes);
      setReportingPeriodFilterValues({ quarter: '', month: '', year: '' });
      if (activeDocumentType) {
        const filteredDocs = docsData.docs.filter((d) => d.display_category === activeDocumentType);
        setFilteredAndSortedDocs(sortByDate(filteredDocs));
      } else {
        setFilteredAndSortedDocs(sortByDate(docsData.docs));
      }
    }
  }, [docsData]);

  const handleCompanyClick = (c) => {
    setActiveCompany(c);
    setActiveDocumentType(null);
    setDocumentTypes([]);
  };

  const handleReportingPeriodChange = (type, value) => {
    const nextFilters = { ...reportingPeriodFilterValues, [type]: value };
    setReportingPeriodFilterValues(nextFilters);
    const filteredDocs = docsData.docs.filter((doc) => {
      if (nextFilters.quarter) {
        return doc.fiscal_quarter === parseInt(nextFilters.quarter, 10);
      }
      if (nextFilters.month) {
        return doc.fiscal_month === parseInt(nextFilters.month, 10);
      }
      if (nextFilters.year) {
        return doc.fiscal_year === parseInt(nextFilters.year, 10);
      }
      return true;
    });
    setFilteredAndSortedDocs(filteredDocs);
  };

  const handleDocumentTypeClick = (clickedDocumentType) => {
    if (clickedDocumentType === activeDocumentType) {
      setActiveDocumentType(null);
      const nextDocs = sortByDate(docsData.docs);
      setFilteredAndSortedDocs(nextDocs);
      return;
    }

    setActiveDocumentType(clickedDocumentType);

    const filteredDocs = docsData.docs.filter(
      (d) => d.display_category?.toLowerCase() === clickedDocumentType.toLowerCase()
    );
    setFilteredAndSortedDocs(sortByDate(filteredDocs));
  };

  const isLoadingDocs = loadingCompanies.includes(activeCompany);

  return (
    <Wrapper>
      <Container top={24}>
        <Container bottom={12}>
          <Body5>Companies / Tags</Body5>
        </Container>
        <TickersButtonWrapper>
          {companies.map((c) => (
            <CompanyButton key={c} company={c} activeCompany={activeCompany} onClick={handleCompanyClick} />
          ))}
        </TickersButtonWrapper>
      </Container>
      {isLoadingDocs ? (
        <Loading />
      ) : (
        <Container top={24}>
          {documentTypes?.length > 0 && (
            <Container>
              <Container bottom={12}>
                <Body5>Document Types</Body5>
              </Container>
              <TickersButtonWrapper>
                {documentTypes.map((v) => (
                  <DocumentTypeButton
                    key={v}
                    value={v}
                    activeDocumentType={activeDocumentType}
                    onClick={handleDocumentTypeClick}
                  />
                ))}
              </TickersButtonWrapper>
            </Container>
          )}
          {Object.values(reportingPeriodTypes).some((v) => v.length > 0) ? (
            <Container top={24}>
              <Container bottom={12}>
                <Body5>Reporting Periods</Body5>
              </Container>
              <TickersButtonWrapper>
                {REPORTING_PERIOD_AVAILABLE_TYPES.map((reportingPeriodType) =>
                  reportingPeriodTypes[reportingPeriodType]?.length > 0 ? (
                    <ReportingPeriodFilter
                      key={reportingPeriodType}
                      reportingPeriodType={reportingPeriodType}
                      options={reportingPeriodTypes[reportingPeriodType]}
                      value={reportingPeriodFilterValues[reportingPeriodType] || noneType}
                      onChange={handleReportingPeriodChange}
                    />
                  ) : null
                )}
              </TickersButtonWrapper>
            </Container>
          ) : null}
          <Container top={24}>
            <SelectableDocumentsList
              docs={filteredAndSortedDocs}
              selectedDocs={selectedDocs}
              onSelect={onSelect}
              onRemoveSelect={onRemoveSelect}
              singleDocMode={singleDocMode}
            />
          </Container>
        </Container>
      )}
    </Wrapper>
  );
};
